import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { UtilityService } from '@eforall/common';
import { AppData, DashboardListItem, DatasetId, Staff } from '@interfaces';
import { FuncService, StaticDataService } from '..';


const EMPTY_DATA: AppData = { userStaffId: 0, staff: [], dashboardItems: [], dashboard: undefined };


export interface DashboardListItemExpanded extends DashboardListItem {
	url: string,
	dataset: string,
	datasetCategory: string,
	dashboardType: string,
	nameWithPossibleTrashIcon: string,
	owner: Staff,
	editor: Staff,
	updatedBy: Staff,
	publishArea: string | undefined,
}


@Injectable({ providedIn: 'root' })
export class AppDataService {

	private readonly util = inject(UtilityService);
	private readonly func = inject(FuncService);
	private readonly staticData = inject(StaticDataService);
	private readonly title = inject(Title);

	private readonly _data = signal<Readonly<AppData>>(EMPTY_DATA);
	public readonly staff = computed<ReadonlyArray<Readonly<Staff>>>(() => this._data().staff);
	public readonly staffById = computed<ReadonlyMap<number, Staff>>(() => new Map(this._data().staff.map(s => [s.staffId, s])));
	public readonly user = computed(() => this.staffById().get(this._data().userStaffId)!);


	constructor() {

		//
		// Update the HTML title (and browser tab) with the
		// latest name whenever the data changes.
		//
		effect(() => {
			const data = this._data();
			if (data.dashboard) this.title.setTitle(data.dashboard.name);
			else this.title.setTitle('EforAll Data Dashboards');
		});

	}


	private readonly allDashboards = computed<ReadonlyArray<Readonly<DashboardListItemExpanded>>>(() => {

		const data = this._data();
		const staticData = this.staticData;
		const staffById = this.staffById();

		/**
		 * Transforms a urlKey and name into a full url to that dashboard. 
		 */
		const createDashboardUrl = (util: UtilityService, urlKey: string, name: string) => {
			const nameSlug = util.url.slugify(name);
			return `https://dashboard.eforall.org/${urlKey.toLowerCase()}/${nameSlug}`;
		}

		return data.dashboardItems.map(d => {
			const item = {
				...d,
				url: createDashboardUrl(this.util, d.urlKey, d.name),
				nameWithPossibleTrashIcon: d.name,
				owner: staffById.get(d.ownerStaffId)!,
				editor: staffById.get(d.editorStaffId)!,
				updatedBy: staffById.get(d.updatedByStaffId)!,
				dataset: staticData.datasets.byId(d.datasetId).name,
				datasetCategory: staticData.dataset_categorys.byId(staticData.datasets.byId(d.datasetId).datasetCategoryId).name,
				dashboardType: staticData.display_types.byId(d.displayTypeId).name,
				publishArea: d.publishAreaId ? staticData.publish_areas.byId(d.publishAreaId).name : undefined,
			};

			//
			// If the dashboard has a trashedUTC value then we prepend a wastebasket character (Unicode U+1F5D1)
			//  to the name.  We use a separate field so that sorting by name is not affected.
			//
			if (item.trashedUTC) item.nameWithPossibleTrashIcon = '🗑 ' + item.name;

			return item;
		});

	});


	/**
	 * Include all available dashboards to this user, except those with inactive
	 * users. Dashboards with inactive users will only exist here if the user has
	 * the canAccessOrphanedDashboards permission.
	 * 
	 * Sorted by updatedUTC desc.
	 */
	public readonly availableDashboards = computed(() => {
		const u = this.user();
		return this
			.allDashboards()
			.filter(d => d.isAvailable)
			.sort((d1, d2) => d2.updatedUTC - d1.updatedUTC);
	});


	/**
	 * Include all dashboards where this user is the owner or the editor.
	 * 
	 * Sorted by dashboard name asc.
	 */
	public readonly personalDashboards = computed(() => {
		const u = this.user();
		return this
			.allDashboards()
			.filter(d => d.isOwnerOrEditor)
			.sort((d1, d2) => d1.name.localeCompare(d2.name));
	});


	/**
	 * Include all dashboards the user accessed in the last 90 days.
	 * 
	 * Sorted by lastAccessUTC desc.
	 */
	public readonly recentDashboards = computed(() => {
		return this
			.allDashboards()
			.filter(d => d.isRecent)
			.sort((d1, d2) => (d2.userLastAccessUTC ?? 0) - (d1.userLastAccessUTC ?? 0));
	});


	/**
	 * Include all dashboards the user has starred.
	 * 
	 * Sorted by dashboard name asc.
	 */
	public readonly starredDashboards = computed(() => {
		return this
			.allDashboards()
			.filter(d => d.isStarred)
			.sort((d1, d2) => d1.name.localeCompare(d2.name));
	});


	/**
	 * Include all dashboards that have a trashedUTC value.
	 * 
	 * Sorted by dashboard name asc.
	 */
	public readonly trashedDashboards = computed(() => {
		return this
			.allDashboards()
			.filter(d => d.isTrashed)
			.sort((d1, d2) => d1.name.localeCompare(d2.name));
	});


	/**
	 * Include all dashboards where the user is no longer active. Dashboards with
	 * inactive users will only exist here if the user has the canAccessOrphanedDashboards
	 * permission.
	 * 
	 * Sorted by dashboard name asc.
	 */
	public readonly orphanedDashboards = computed(() => {
		return this
			.allDashboards()
			.filter(d => d.isOrphaned)
			.sort((d1, d2) => d1.name.localeCompare(d2.name));
	});


	public readonly specificDatasetFilter = signal(DatasetId.AiAdvisor);


	/**
	 * Include all dashboards that match the specificDatasetsFilter.
	 * 
	 * Sorted by dashboard name asc.
	 */
	public readonly specificDatasetDashboards = computed(() => {
		const datasetId = this.specificDatasetFilter();
		return this
			.allDashboards()
			.filter(d => d.datasetId == datasetId)
			.sort((d1, d2) => d1.name.localeCompare(d2.name));
	});


	/**
	 * After each function call, set the returned AppData here and the
	 * changes will flow through the computed signals.
	 */
	public async setLatest(data: AppData): Promise<void> {
		this._data.set(data);
	}


	/**
	 * This is called when the user logs out, so there isn't a flash of the
	 * last user's data while the new user reloads after logging back in.
	 */
	public async clearData(): Promise<void> {
		this._data.set(EMPTY_DATA);
	}

}